import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { BellOff, Check, ChevronLeft, Pause, Pencil, Play, X } from 'lucide-react';
import {
  useDeviceDetails,
  usePausePolling,
  useResumePolling,
  useUpdateDevice,
  type DeviceDetails as DeviceDetailsData,
  type DeviceGatewayInfo,
} from '@/hooks/useDeviceDetails';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeviceConnectionMethods } from '@/hooks/useDeviceConnectionMethods';
import { useBulkSilenceDevice } from '@/hooks/useAlertOverrides';
import ConfigTimeline from '@/components/ConfigTimeline';
import ConnectionsPanel from '@/components/ConnectionsPanel';
import DeviceActions from '@/components/DeviceActions';
import DeviceHealth from '@/components/DeviceHealth';
import DeviceReadinessBanner from '@/components/DeviceReadinessBanner';
import DeviceIncidents from '@/components/DeviceIncidents';
import DeviceLldpTab from '@/components/DeviceLldpTab';
import DevicePortsTab from '@/components/DevicePortsTab';
import DeviceAddressesSection from '@/components/DeviceAddressesSection';
import NetworkServicesSection from '@/components/NetworkServicesSection';
import DeviceRoutesTab from '@/components/DeviceRoutesTab';
import DeviceFirewallTab from '@/components/DeviceFirewallTab';
import { useFirewallRules } from '@/hooks/useFirewallRules';
import { usePermission } from '@/hooks/usePermission';
import { GatewayVpnTab } from '@/features/vpn';
import DeviceStpTab from '@/components/DeviceStpTab';
import DeviceTerminal from '@/components/DeviceTerminal';
import DeviceVlansTab from '@/components/DeviceVlansTab';
import DeviceWirelessTab from '@/components/DeviceWirelessTab';
import DeviceWebProxy from '@/components/DeviceWebProxy';
import DeviceWebCaptures from '@/components/DeviceWebCaptures';
import DeviceCaptureRequestBanner from '@/components/DeviceCaptureRequestBanner';
import DeviceActivateForm from '@/components/DeviceActivateForm';
import SessionRecordings from '@/components/SessionRecordings';
import SilenceAlertsModal from '@/components/SilenceAlertsModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeviceTrafficTab } from '@/components/traffic/DeviceTrafficTab';
import { PageContainer } from '@/components/ui/page-container';
import { deviceTypeLabel, isWirelessAp } from '@/types/deviceType';
import { useDeviceWireless } from '@/hooks/useDeviceWireless';
import { cn } from '@/lib/utils';

/**
 * DeviceDetails wrapper migration. Spec §4 MR (mobile-readonly).
 *
 * 13 tabs (terminal + web-ui conditional on connection method). URL is
 * the single source of truth via `?tab=…`; the default tab keeps the
 * URL clean (no `?tab=overview`).
 *
 * This migration ports the OUTER SHELL only — the per-tab content
 * components (ConfigTimeline, ConnectionMethods, DevicePortsTab, etc.)
 * stay Bulma for now. Each will get its own migration commit. They
 * render fine inside the new Tailwind shell because Bulma is layered
 * (Phase 1 fix).
 *
 * Overview tab is migrated inline — it's a small device-info dl with
 * an inline IP-edit affordance and a NETCONF toggle. Both ported to
 * shadcn primitives (Input, Switch).
 *
 * Mobile tab strip: scrolls horizontally; the TabsList has overflow-x-auto
 * so the 11-13 tabs don't truncate on a 360px viewport. DO-only tabs
 * (terminal, web-ui, actions per spec §4.1) stay visible but their
 * embedded components handle their own DO empty-states on mobile.
 */

type Tab =
  | 'overview'
  | 'incidents'
  | 'health'
  | 'config-timeline'
  | 'actions'
  | 'connections'
  | 'ports'
  | 'vlans'
  | 'stp'
  | 'routes'
  | 'firewall'
  | 'lldp'
  | 'traffic'
  | 'wireless'
  | 'recordings'
  | 'terminal'
  | 'web-ui'
  | 'vpn';

const VALID_TABS: readonly Tab[] = [
  'overview',
  'incidents',
  'traffic',
  'health',
  'config-timeline',
  'actions',
  'connections',
  'ports',
  'vlans',
  'stp',
  'routes',
  'firewall',
  'lldp',
  'wireless',
  'recordings',
  'terminal',
  'web-ui',
  'vpn',
];

interface TabSpec {
  id: Tab;
  label: string;
}

const TAB_SPECS: TabSpec[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'incidents', label: 'Incidents' },
  // Third, and directly after Incidents, because tab order is a claim about
  // what follows what and the triage path is Incidents -> Traffic.
  { id: 'traffic', label: 'Traffic' },
  { id: 'health', label: 'Health' },
  { id: 'config-timeline', label: 'Config timeline' },
  { id: 'actions', label: 'Actions' },
  { id: 'connections', label: 'Connections' },
  { id: 'ports', label: 'Ports' },
  { id: 'vlans', label: 'VLANs' },
  { id: 'stp', label: 'STP' },
  { id: 'routes', label: 'Routes' },
  { id: 'lldp', label: 'LLDP' },
  { id: 'recordings', label: 'Recordings' },
];

function isValidTab(value: string | null): value is Tab {
  return value != null && (VALID_TABS as readonly string[]).includes(value);
}

function statusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === 'online') return 'bg-sev-4/15 text-sev-4';
  if (s === 'offline') return 'bg-sev-1/15 text-sev-1';
  // `unknown` is a real answer, not a missing one: nobody is currently in a
  // position to say whether the device is alive. Neutral, never a warning tone —
  // the device may be perfectly healthy.
  if (s === 'unknown') return 'bg-muted text-muted-foreground';
  return 'bg-sev-3/20 text-foreground';
}

export default function DeviceDetails() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = isValidTab(tabParam) ? tabParam : 'overview';

  const setActiveTab = (next: string): void => {
    if (!isValidTab(next) || next === activeTab) return;
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next === 'overview') {
          updated.delete('tab');
        } else {
          updated.set('tab', next);
        }
        return updated;
      },
      { replace: false },
    );
  };

  const [showActivate, setShowActivate] = useState(false);
  const [showSilenceModal, setShowSilenceModal] = useState(false);
  const bulkSilenceMutation = useBulkSilenceDevice();

  const { data: device, isLoading, isError } = useDeviceDetails({ deviceId: id ?? null });
  const updateDevice = useUpdateDevice(id ?? '');
  const pausePolling = usePausePolling(id ?? '');
  const resumePolling = useResumePolling(id ?? '');
  const { data: connectionMethods } = useDeviceConnectionMethods(id ?? '');
  // Shares its cache with the Wireless tab's own query, so this costs nothing
  // extra — it just lets the tab list know whether there's anything to show.
  const { data: wireless } = useDeviceWireless(id);
  const hasWirelessRadios = (wireless?.radios.length ?? 0) > 0;
  // Same trick for the Firewall tab: it appears only where we actually read
  // the rules. "No parser for this vendor" is a coverage gap, and coverage
  // gaps stay out of customer UIs.
  const { data: firewallRules } = useFirewallRules(id);
  const hasModeledFirewall = firewallRules?.enforcement === 'modeled';

  const hasTerminalConnectionMethod = useMemo(
    () =>
      connectionMethods?.some(
        (cm) =>
          cm.sessionType === 'ssh' ||
          cm.sessionType === 'telnet' ||
          cm.sessionType === 'console',
      ) ?? false,
    [connectionMethods],
  );
  // A terminal or the web UI is a session on the device, often logged in with
  // a stored credential, so both take their own permission, which the backend
  // checks too (website-backend#82).
  const canRemoteAccess = usePermission('device:remote_access');
  const canReadVpn = usePermission('vpn:read');
  const showTerminal = hasTerminalConnectionMethod && canRemoteAccess;
  const showWebUi = !!device?.protocolSetup?.webUiBrowsable && canRemoteAccess;

  const [isEditingIp, setIsEditingIp] = useState(false);
  const [editIpValue, setEditIpValue] = useState('');

  const [syncedDevice, setSyncedDevice] = useState<typeof device>(undefined);
  if (device !== syncedDevice) {
    setSyncedDevice(device);
    if (device) setEditIpValue(device.ipAddress ?? '');
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-6 w-64 mb-3" />
        <Skeleton className="h-9 w-80 mb-2" />
        <Skeleton className="h-4 w-48 mb-6" />
        <Skeleton className="h-10 w-full mb-5" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError || !device || !id) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load device details.</AlertDescription>
        </Alert>
        <Button variant="outline" asChild>
          <Link to="/network?tab=equipment">
            <ChevronLeft className="w-4 h-4" />
            Back to equipment
          </Link>
        </Button>
      </PageContainer>
    );
  }

  // Build the list of tabs to render. Wireless appears for APs, and for any
  // other device we actually collected radios from — a 5G home gateway is a
  // *router* that is also an AP, so a type check alone would hide its WLANs.
  // Terminal when a CLI connection method exists. Web UI keys off the weak,
  // default-on `webUiBrowsable` — an http(s) method exists and no profile has
  // declared the family serves no UI — deliberately NOT the stricter
  // `supportsWebUi`/`cgi:`. An unidentified device has no profile at all, and
  // those are exactly the ones someone needs to open in order to help us
  // support them, so requiring a profile to opt in would exclude the whole
  // population the surface exists for.
  const isWirelessDevice = isWirelessAp(device.deviceType) || hasWirelessRadios;
  // This device row IS a gateway (devices.id == gateways.id) — server-resolved,
  // not the mutable `type` string. Gateways keep the full tab set (uniform with
  // other devices; the tabs simply stay empty), but the polled-device chrome —
  // the Activate flow, the "Polled by" framing — doesn't apply.
  const gatewayInfo = device.gateway ?? null;
  const visibleTabs: TabSpec[] = [
    ...TAB_SPECS,
    ...(isWirelessDevice ? [{ id: 'wireless' as Tab, label: 'Wireless' }] : []),
    ...(showTerminal ? [{ id: 'terminal' as Tab, label: 'Terminal' }] : []),
    ...(showWebUi ? [{ id: 'web-ui' as Tab, label: 'Web UI' }] : []),
    ...(hasModeledFirewall ? [{ id: 'firewall' as Tab, label: 'Firewall' }] : []),
    // The technician VPN runs on gateways (docs/VpnAccess.md, "Slice 6 design").
    ...(gatewayInfo && canReadVpn ? [{ id: 'vpn' as Tab, label: 'VPN' }] : []),
  ];

  return (
    <PageContainer>
      {/* Breadcrumb / back chevron */}
      <nav
        className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-3"
        aria-label="Breadcrumb"
      >
        <Link to="/network?tab=equipment" className="hover:text-foreground">
          Network equipment
        </Link>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className="text-foreground truncate">
          {device.name || device.ipAddress || 'Device'}
        </span>
      </nav>
      <Link
        to="/network?tab=equipment"
        className="md:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" />
        All equipment
      </Link>

      {/* Honest "is this device actually being monitored?" banner (no/offline
          gateway, or a credential whose secret never reached a gateway). */}
      <DeviceReadinessBanner readiness={device.readiness} className="mb-4" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight break-words">
            {device.name || 'Unnamed device'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
            {device.ipAddress ? (
              <span className="font-mono text-muted-foreground">{device.ipAddress}</span>
            ) : null}
            <Badge
              className={cn('border-0 font-normal capitalize', statusBadgeClass(device.status))}
            >
              {device.status}
            </Badge>
            {device.deviceType ? (
              <Badge variant="secondary" className="font-normal">
                {deviceTypeLabel(device.deviceType)}
              </Badge>
            ) : null}
            {/*
              A pause is invisible otherwise: the device keeps its status and
              simply stops producing data, which is indistinguishable from a
              quiet one. Say so, and say when it ends — a forgotten indefinite
              pause is how a device silently stops being monitored.
            */}
            {device.pollingPaused ? (
              <Badge variant="outline" className="font-normal gap-1">
                <Pause className="w-3 h-3" />
                {device.pollingPausedUntil
                  ? `Polling paused until ${new Date(device.pollingPausedUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                  : 'Polling paused'}
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" onClick={() => { setShowSilenceModal(true); }}>
            <BellOff className="w-4 h-4" />
            <span className="hidden md:inline">Silence alerts</span>
            <span className="md:hidden">Silence</span>
          </Button>
          {/*
            Polling pause. Offered on every device but aimed at firmware that
            allows one administrative session at a time — the gateway's polling
            and an operator in the device's web UI evict each other, and on
            Netgear Insight the resulting failed logins lock the admin account
            for everyone. Pausing stops the collectors; the Web UI tab below
            keeps working.
          */}
          {gatewayInfo ? null : device.pollingPaused ? (
            <Button
              variant="outline"
              disabled={resumePolling.isPending}
              onClick={() => { resumePolling.mutate(); }}
            >
              <Play className="w-4 h-4" />
              <span className="hidden md:inline">Resume polling</span>
              <span className="md:hidden">Resume</span>
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={pausePolling.isPending}>
                  <Pause className="w-4 h-4" />
                  <span className="hidden md:inline">Pause polling</span>
                  <span className="md:hidden">Pause</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { pausePolling.mutate({ minutes: 60 }); }}>
                  For 1 hour
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { pausePolling.mutate({ minutes: 8 * 60 }); }}>
                  For 8 hours
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { pausePolling.mutate({}); }}>
                  Until I resume
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* Activation configures polling; a gateway reports itself. */}
          {gatewayInfo ? null : (
            <Button onClick={() => { setShowActivate(true); }}>
              Activate
            </Button>
          )}
        </div>
      </div>

      <DeviceCaptureRequestBanner
        deviceId={id}
        webUiAvailable={showWebUi}
        // Suppressed on the Web UI tab, which makes the offer inline with the
        // consent and controls — two asks on one screen is nagging.
        hidden={activeTab === 'web-ui'}
        onGoToWebUi={() => { setActiveTab('web-ui'); }}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full overflow-x-auto justify-start mb-5">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="flex-shrink-0">
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Device information</h2>
            <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
              <InfoRow label="Name" value={device.name || '—'} />
              <InfoRow label="Description" value={device.description ?? '—'} />
              <InfoRow
                label="IP address"
                value={
                  isEditingIp ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="text"
                        value={editIpValue}
                        onChange={(e) => { setEditIpValue(e.target.value); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateDevice.mutate(
                              { ipAddr: editIpValue },
                              { onSuccess: () => { setIsEditingIp(false); } },
                            );
                          } else if (e.key === 'Escape') {
                            setEditIpValue(device.ipAddress ?? '');
                            setIsEditingIp(false);
                          }
                        }}
                        autoFocus
                        className="h-8 max-w-[16rem] font-mono"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          updateDevice.mutate(
                            { ipAddr: editIpValue },
                            { onSuccess: () => { setIsEditingIp(false); } },
                          );
                        }}
                        disabled={updateDevice.isPending}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditIpValue(device.ipAddress ?? '');
                          setIsEditingIp(false);
                        }}
                        aria-label="Cancel edit"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setIsEditingIp(true); }}
                      title="Click to edit IP address"
                      className="inline-flex items-center gap-1.5 font-mono text-left hover:text-foreground border-b border-dashed border-border-subtle hover:border-foreground transition-colors"
                    >
                      {device.ipAddress || '—'}
                      <Pencil className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                    </button>
                  )
                }
              />
              <InfoRow
                label="Status"
                value={
                  <Badge
                    className={cn(
                      'border-0 font-normal capitalize',
                      statusBadgeClass(device.status),
                    )}
                  >
                    {device.status}
                  </Badge>
                }
              />
              <InfoRow label="Device type" value={device.deviceType ?? '—'} />
              <InfoRow
                label="Polled by"
                value={
                  gatewayInfo ? (
                    <span className="text-muted-foreground">
                      Self-reported (gateway heartbeat)
                    </span>
                  ) : device.assignedGatewayId ? (
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {device.assignedGatewayName ?? `Gateway ${device.assignedGatewayId.slice(0, 8)}`}
                      {device.assignedGatewayStatus ? (
                        <Badge
                          className={cn(
                            'border-0 font-normal capitalize',
                            statusBadgeClass(device.assignedGatewayStatus),
                          )}
                        >
                          {device.assignedGatewayStatus}
                        </Badge>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )
                }
              />
              {device.locatedViaFdb?.length ? (
                <InfoRow
                  label="Connected to"
                  value={
                    <span className="flex flex-col gap-1">
                      {device.locatedViaFdb.map((loc) => (
                        <span
                          key={`${loc.linkId ?? ''}:${loc.deviceId}:${loc.interfaceId ?? ''}`}
                          className="inline-flex items-center gap-1.5 flex-wrap"
                        >
                          {loc.viaInterfaceName ? (
                            <span className="font-mono text-xs text-muted-foreground">
                              {loc.viaInterfaceName} →
                            </span>
                          ) : null}
                          <Link to={`/devices/${loc.deviceId}`} className="hover:underline">
                            {loc.deviceName ?? loc.deviceId}
                          </Link>
                          {loc.interfaceName ? (
                            <span className="font-mono text-xs text-muted-foreground">
                              :{loc.interfaceName}
                            </span>
                          ) : null}
                          {loc.behindDeviceId ? (
                            <span
                              className="text-xs text-muted-foreground"
                              title="This port learned the device's MAC through another device; nothing else places it"
                            >
                              behind{' '}
                              <Link to={`/devices/${loc.behindDeviceId}`} className="hover:underline">
                                {loc.behindDeviceName ?? loc.behindDeviceId}
                              </Link>
                            </span>
                          ) : null}
                          {loc.method === 'fdb_edge' && !loc.linkId ? (
                            <span
                              className="text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                              title="Inferred from the switch port that learned this device's MAC (fewest-MAC edge port)"
                            >
                              assumed
                            </span>
                          ) : null}
                        </span>
                      ))}
                    </span>
                  }
                />
              ) : null}
              <InfoRow label="Region" value={device.regionName} />
              <InfoRow label="Division" value={device.divisionName ?? '—'} />
              <InfoRow
                label="Created"
                value={new Date(device.createdAt).toLocaleString()}
              />
              <InfoRow
                label="Updated"
                value={new Date(device.updatedAt).toLocaleString()}
              />
            </dl>
          </section>

          <DeviceAddressesSection deviceId={id} />

          {gatewayInfo ? <GatewaySection gateway={gatewayInfo} /> : null}

          {gatewayInfo && id ? <NetworkServicesSection gatewayId={id} /> : null}

          <HardwareSection device={device} isGateway={gatewayInfo != null} />
        </TabsContent>

        <TabsContent value="incidents">
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Incidents</h2>
            <DeviceIncidents deviceId={id} />
          </section>
        </TabsContent>

        <TabsContent value="traffic">
          <DeviceTrafficTab
            deviceId={device.id}
            deviceType={device.deviceType ?? null}
            incidentId={searchParams.get('incident')}
          />
        </TabsContent>

        <TabsContent value="health">
          <DeviceHealth deviceId={id} />
        </TabsContent>

        {/* Sub-component tabs render their existing Bulma markup
            unchanged. Each gets its own migration commit later. */}
        <TabsContent value="config-timeline">
          <ConfigTimeline deviceId={id} />
        </TabsContent>
        <TabsContent value="actions">
          <DeviceActions deviceId={id} />
        </TabsContent>
        <TabsContent value="connections">
          <ConnectionsPanel
            deviceId={id}
            regionId={device.regionId}
            divisionId={device.divisionId}
            deviceIp={device.ipAddress}
          />
        </TabsContent>
        <TabsContent value="ports">
          <DevicePortsTab deviceId={id} />
        </TabsContent>
        <TabsContent value="vlans">
          <DeviceVlansTab deviceId={id} />
        </TabsContent>
        <TabsContent value="stp">
          <DeviceStpTab deviceId={id} />
        </TabsContent>
        <TabsContent value="routes">
          <DeviceRoutesTab deviceId={id} />
        </TabsContent>
        {/* Ungated even without its trigger: a ?tab=firewall deep link to a
            device we cannot read renders the honest reason, not a blank. */}
        <TabsContent value="firewall">
          <DeviceFirewallTab deviceId={id} />
        </TabsContent>
        <TabsContent value="lldp">
          <DeviceLldpTab deviceId={id} />
        </TabsContent>
        {isWirelessDevice ? (
          <TabsContent value="wireless">
            <DeviceWirelessTab deviceId={id} />
          </TabsContent>
        ) : null}
        {gatewayInfo && canReadVpn && id ? (
          <TabsContent value="vpn">
            <GatewayVpnTab gatewayId={id} />
          </TabsContent>
        ) : null}
        <TabsContent value="recordings">
          {/* Web-UI captures live here rather than beside the proxy that
              produces them: this is the surface for deleting one, and the
              Web UI tab disappears the moment a device stops being
              browsable — which would take the only way to withdraw a
              shared capture with it. */}
          <div className="space-y-6">
            <SessionRecordings deviceId={id} />
            <DeviceWebCaptures deviceId={id} />
          </div>
        </TabsContent>
        {showTerminal ? (
          <TabsContent value="terminal">
            <DeviceTerminal deviceId={id} />
          </TabsContent>
        ) : null}
        {showWebUi && device.protocolSetup ? (
          <TabsContent value="web-ui">
            {/* Offer to record only for devices we could not identify —
                those are the ones we need help with, and the ones where
                interrupting the user is justified.

                Gated on whether a vendor profile resolved, not on any
                `supports*` flag. This read `!supportsWebUi`, which asks
                whether the profile has a `cgi:` block — a much narrower
                question. A device can be fully identified and fully
                supported without one (an EdgeRouter's profile is
                `cli:`-only), so that gate asked customers to help us
                support hardware we already support. */}
            <DeviceWebProxy
              deviceId={id}
              offerCapture={!device.protocolSetup.profileMatched}
            />
          </TabsContent>
        ) : null}
      </Tabs>

      {showActivate ? (
        <DeviceActivateForm
          organizationId={device.organizationId}
          deviceId={id}
          initial={{
            name: device.name,
            ipAddress: device.ipAddress ?? null,
            regionId: device.regionId,
            divisionId: device.divisionId ?? null,
            description: device.description ?? null,
          }}
          onClose={() => { setShowActivate(false); }}
        />
      ) : null}

      <SilenceAlertsModal
        isActive={showSilenceModal}
        onClose={() => { setShowSilenceModal(false); }}
        onSilence={(silenceUntil, reason) => {
          bulkSilenceMutation.mutate(
            { deviceId: id, data: { silenceUntil, silenceReason: reason || undefined } },
            { onSuccess: () => { setShowSilenceModal(false); } },
          );
        }}
        isPending={bulkSilenceMutation.isPending}
      />
    </PageContainer>
  );
}

// ─── Management-protocol callouts (NETCONF / REST API) ───────────────


// ─── Info-row helper ─────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground font-medium">{label}</dt>
      <dd className="break-words min-w-0">{value}</dd>
    </>
  );
}

/** The gateway behind a gateway's own device row: heartbeat-based liveness and
 *  the facts the gateway reports about itself. Gateways aren't polled, so this
 *  is the gateway-flavored counterpart of the polling info other devices get. */
function GatewaySection({ gateway }: { gateway: DeviceGatewayInfo }) {
  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold">Gateway</h2>
        <Link
          to="/gateways"
          className="text-sm font-medium text-muted-foreground hover:text-foreground underline underline-offset-2"
        >
          Manage gateway
        </Link>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        This device is the Witina gateway itself. It reports its own status over
        its heartbeat rather than being polled, and it needs no credentials.
      </p>
      <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
        <InfoRow
          label="Status"
          value={
            <Badge className={cn('border-0 font-normal capitalize', statusBadgeClass(gateway.status))}>
              {gateway.status}
            </Badge>
          }
        />
        <InfoRow
          label="Last heartbeat"
          value={gateway.lastSeen ? new Date(gateway.lastSeen).toLocaleString() : '—'}
        />
        <InfoRow
          label="Software version"
          value={
            gateway.softwareVersion ? (
              <span className="font-mono">{gateway.softwareVersion}</span>
            ) : (
              '—'
            )
          }
        />
        <InfoRow
          label="Architecture"
          value={
            gateway.architecture ? (
              <span className="font-mono">{gateway.architecture}</span>
            ) : (
              '—'
            )
          }
        />
      </dl>
    </section>
  );
}

function HardwareSection({ device, isGateway }: { device: DeviceDetailsData; isGateway: boolean }) {
  const rows: { label: string; value: string | null; mono?: boolean }[] = [
    { label: 'Vendor', value: device.vendor },
    { label: 'Manufacturer', value: device.manufacturer },
    { label: 'Model', value: device.model },
    { label: 'Hostname', value: device.hostname },
    { label: 'Serial number', value: device.serialNumber, mono: true },
    { label: 'Chassis MAC', value: device.chassisMac, mono: true },
    { label: 'Software version', value: device.softwareVersion, mono: true },
    { label: 'Firmware version', value: device.firmwareVersion, mono: true },
    { label: 'sysObjectID', value: device.sysObjectId, mono: true },
  ];

  const hasAnyValue = rows.some((r) => r.value != null && r.value !== '');

  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <h2 className="text-sm font-semibold mb-4">Hardware &amp; firmware</h2>
      {hasAnyValue ? (
        <dl className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-6 gap-y-3 text-sm">
          {rows.map((r) => (
            <InfoRow
              key={r.label}
              label={r.label}
              value={
                r.value ? (
                  <span className={r.mono ? 'font-mono' : undefined}>{r.value}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
          ))}
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">
          {isGateway
            ? 'Hardware details are not collected for the gateway itself — its software version and architecture are shown in the Gateway section above.'
            : 'No hardware or firmware details have been collected for this device yet. They are populated automatically once the device is reachable via SNMP or a vendor profile.'}
        </p>
      )}
    </section>
  );
}
